/**
 * Insight cards: the short "here is what changed and what to do" tiles above
 * the intelligence panels.
 *
 * Same three-mechanism grounding as the MCP analysis layer, because it is the
 * same risk. A card is read in two seconds and believed, which makes it the
 * worst possible place for a confident invented figure.
 *
 *   1. The model never receives raw data, only pre-computed Facts.
 *   2. It cannot write a number: to cite one it emits {key}, and the server
 *      substitutes the measured display string.
 *   3. Anything numeric surviving substitution was invented, and the card is
 *      dropped rather than shown.
 *
 * Mechanism 3 drops the card instead of the whole batch. One hallucinated tile
 * should not cost the user four good ones.
 */

import {
  containsInventedNumber,
  substituteFacts,
  type Fact,
} from "@carrier/ocs-client";

export type InsightSeverity = "critical" | "attention" | "healthy" | "info";

export interface InsightCard {
  title: string;
  body: string;
  severity: InsightSeverity;
  /** Fact key this card leads with, so the UI can show the figure prominently. */
  metricKey?: string;
  /** What to do about it. Absent when nothing is warranted. */
  action?: string;
}

/**
 * Every field is a string or an enum. There is deliberately no numeric field:
 * adding one would hand the model a legitimate channel for a number it made up.
 * A test asserts this shape stays free of them.
 */
export const INSIGHTS_TOOL_SCHEMA = {
  name: "report_insights",
  description:
    "Report 2-4 insight cards about this operator's fleet. Cite every figure as " +
    "a {placeholder} using the fact's key — never write a number yourself.",
  input_schema: {
    type: "object" as const,
    properties: {
      cards: {
        type: "array",
        description:
          "2-4 cards, most important first. Fewer is better than padding with " +
          "cards that say nothing.",
        items: {
          type: "object",
          properties: {
            title: {
              type: "string",
              description: "Under 8 words. The finding, not a category label.",
            },
            body: {
              type: "string",
              description:
                "1-2 sentences on what it means. Cite figures as {key}.",
            },
            severity: {
              type: "string",
              enum: ["critical", "attention", "healthy", "info"],
            },
            metric_key: {
              type: "string",
              description: "Key of the single fact this card is about.",
            },
            action: {
              type: "string",
              description:
                "One concrete thing to do. Omit when no action is warranted.",
            },
          },
          required: ["title", "body", "severity"],
        },
      },
    },
    required: ["cards"],
  },
} as const;

export const INSIGHTS_SYSTEM_PROMPT = `You write short insight cards for an eSIM fleet operator's dashboard. You are given facts that have ALREADY been measured. Your job is judgement, not arithmetic.

Rules:
1. NEVER write a number, percentage, or quantity. To cite one, use its placeholder: {key}. The system substitutes the measured value.
2. Only cite facts from the supplied list. Do not infer a figure you were not given.
3. Say plainly when the facts show nothing worth acting on. A single "healthy" card is a good answer for a healthy fleet.
4. Do not restate the facts — the operator can see the table. Say what they mean.
5. Under 8 words in a title. One or two sentences in a body. No filler, no marketing words.`;

/** Shape returned by the forced tool call, before validation. */
interface RawCard {
  title?: unknown;
  body?: unknown;
  severity?: unknown;
  metric_key?: unknown;
  action?: unknown;
}

const SEVERITIES: readonly InsightSeverity[] = ["critical", "attention", "healthy", "info"];

/**
 * Validate, substitute, and drop anything that invented a figure.
 *
 * Exported separately from the model call so the guard is testable without
 * Bedrock — the guard is the part that must not regress quietly.
 */
export function materializeCards(
  toolInput: Record<string, unknown>,
  facts: Fact[],
): InsightCard[] {
  const raw = Array.isArray(toolInput.cards) ? (toolInput.cards as RawCard[]) : [];
  const factKeys = new Set(facts.map((f) => f.key));
  const cards: InsightCard[] = [];

  for (const c of raw) {
    if (typeof c.title !== "string" || typeof c.body !== "string") continue;
    const severity = SEVERITIES.includes(c.severity as InsightSeverity)
      ? (c.severity as InsightSeverity)
      : "info";

    const title = substituteFacts(c.title, facts);
    const body = substituteFacts(c.body, facts);
    const action = typeof c.action === "string" ? substituteFacts(c.action, facts) : undefined;

    // Mechanism 3, per card.
    const rendered = [title, body, action ?? ""].join(" ");
    if (containsInventedNumber(rendered, facts)) continue;

    cards.push({
      title,
      body,
      severity,
      ...(typeof c.metric_key === "string" && factKeys.has(c.metric_key)
        ? { metricKey: c.metric_key }
        : {}),
      ...(action ? { action } : {}),
    });
  }

  return cards.slice(0, 4);
}

/**
 * The deterministic cards shown when the model path is unavailable — no
 * credentials, allowance spent, Bedrock down, or a free-tier caller.
 *
 * The dashboard must never be empty because AI was unavailable. These are the
 * measured facts, presented plainly and labelled as not AI-generated by the UI.
 */
export function fallbackCards(sections: Array<{ title: string; facts: Fact[] }>): InsightCard[] {
  return sections
    .filter((s) => s.facts.length > 0)
    .slice(0, 4)
    .map((s) => {
      const lead = s.facts[0]!;
      return {
        title: s.title,
        body: `${lead.label}: ${lead.display}.`,
        severity: "info" as const,
        metricKey: lead.key,
      };
    });
}
